/**
 * Media-capture repository — the single data-access seam for the Nest-only
 * `media-capture` slice (structured unit / building photo shoots).
 *
 * NestJS is the system of record (`app.media_capture_sessions` /
 * `app.media_items` — Prisma-owned, no Supabase table), so there is no
 * Supabase fallback. Wire shapes mirror apps/api/src/media-capture
 * (controller + DTOs + MediaUploadsService) field-for-field (snake_case).
 *
 * Two bindings, like units.ts: the workspace / original-url READS carry
 * RowScopeGuard server-side (403 without x-active-role), every write is
 * capability-gated only and must never be blocked on a missing role.
 */
import { z } from "zod";
import type { BilingualLabel, MediaCategory, ShotRule, ShotSlot } from "@dallali/shared";
import { makeNestRequest } from "@/services/nestRequest";
import { API_ROUTES } from "@/api/routes";

const nestRequest = makeNestRequest("media-capture");
const nestRequestScoped = makeNestRequest("media-capture", {
  forwardActiveRole: true,
  requireActiveRole: true,
});
/** `api` part transport: raw bytes, not JSON. Retries are the uploader's job. */
const nestRequestBinary = makeNestRequest("media-capture", { json: false });

export type MediaEntityType = "unit" | "property";
export type MediaKind = "photo" | "video" | "plan" | "site_sketch" | "three_d";
export type MediaVisibility = "public" | "internal";
export type MediaUploadState = "pending" | "uploaded" | "processing" | "ready" | "failed";
export type MediaItemStatus = "draft" | "in_review" | "approved" | "rejected";
export type CaptureSessionStatus = "open" | "in_review" | "changes_requested" | "approved";

/** Mirrors the Prisma `MediaRejectReason` enum (review workflow, PR 4a). */
export const MEDIA_REJECT_REASONS = [
  "blurry",
  "dark",
  "tilted",
  "portrait",
  "people_or_personal_items",
  "missing_or_cut_video",
  "compressed_file",
  "wrong_space",
  "other",
] as const;
export type MediaRejectReason = (typeof MEDIA_REJECT_REASONS)[number];

// ── Wire shapes ────────────────────────────────────────────────────────────

const mediaItemSchema = z
  .object({
    id: z.string(),
    entity_type: z.enum(["unit", "property"]),
    entity_id: z.string(),
    session_id: z.string().nullable(),
    kind: z.enum(["photo", "video", "plan", "site_sketch", "three_d"]),
    family: z.string(),
    category_key: z.string(),
    slot_key: z.string().nullable(),
    visibility: z.enum(["public", "internal"]),
    status: z.string(),
    is_cover: z.boolean(),
    sort_order: z.number(),
    upload_state: z.enum(["pending", "uploaded", "processing", "ready", "failed"]),
    processing_error: z.string().nullable(),
    web_url: z.string().nullable(),
    poster_url: z.string().nullable(),
    mime_type: z.string().nullable(),
    bytes: z.number().nullable(),
    file_name: z.string().nullable(),
    legacy_source_url: z.string().nullable(),
    created_at: z.string(),
    // Review fields (PR 4a) — optional so older payloads / fixtures still parse.
    reject_reason: z.string().nullable().optional(),
    review_note: z.string().nullable().optional(),
    reviewed_at: z.string().nullable().optional(),
  })
  .passthrough();
export type MediaItem = z.infer<typeof mediaItemSchema>;

const referenceMeasurementSchema = z.object({
  label: z.string(),
  length_m: z.number(),
  width_m: z.number(),
});
export type ReferenceMeasurement = z.infer<typeof referenceMeasurementSchema>;

export interface CaptureSessionFlags {
  no_power?: boolean;
  defects_found?: boolean;
  rescheduled?: boolean;
}

const captureSessionSchema = z
  .object({
    updated_at: z.string().optional(),
    id: z.string(),
    entity_type: z.enum(["unit", "property"]),
    entity_id: z.string(),
    status: z.string(),
    started_at: z.string(),
    reference_measurements: z.array(referenceMeasurementSchema),
    notes: z.string().nullable(),
    flags: z.record(z.boolean()),
    // Review fields (PR 4a) — optional so older payloads / fixtures still parse.
    agent_id: z.string().optional(),
    submitted_at: z.string().nullable().optional(),
    reviewed_at: z.string().nullable().optional(),
    review_note: z.string().nullable().optional(),
    reshoot_due_at: z.string().nullable().optional(),
    phone_delete_due_at: z.string().nullable().optional(),
    gallery_sync_pending: z.boolean().optional(),
  })
  .passthrough();
export type CaptureSession = z.infer<typeof captureSessionSchema> & { flags: CaptureSessionFlags };

const labelSchema = z.object({ ar: z.string(), en: z.string() });
const slotSchema = z
  .object({
    slotKey: z.string(),
    spaceKey: z.string(),
    index: z.number().optional(),
    label: labelSchema,
    categoryKey: z.string(),
    shots: z.object({ min: z.number(), max: z.number() }),
    rule: z.enum(["required", "if_present", "optional", "internal"]),
  })
  .passthrough();

const workspaceSchema = z.object({
  unit: z
    .object({
      id: z.string(),
      unit_number: z.string().nullable(),
      unit_type: z.string().nullable(),
      short_code: z.string().nullable(),
    })
    .optional(),
  property: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      property_type: z.string().nullable(),
      short_code: z.string().nullable(),
    })
    .optional(),
  family: z.string(),
  checklist: z.object({
    family: z.string(),
    categories: z.array(z.object({ key: z.string(), label: labelSchema })),
    slots: z.array(slotSchema),
  }),
  progress: z.object({
    slots: z.array(
      z.object({
        slot_key: z.string(),
        rule: z.enum(["required", "if_present", "optional", "internal"]),
        required: z.number(),
        count: z.number(),
        done: z.boolean(),
      }),
    ),
    overall: z.object({
      required_done: z.number(),
      required_total: z.number(),
      required_slots_done: z.number(),
      required_slots_total: z.number(),
    }),
  }),
  items: z.array(mediaItemSchema),
  legacy_images: z.array(
    z.object({ url: z.string(), kind: z.string(), category_key: z.string() }),
  ),
  active_session: captureSessionSchema.nullable(),
  last_approved_session: captureSessionSchema.nullable().optional(),
});

export interface SlotProgress {
  slot_key: string;
  rule: ShotRule;
  required: number;
  count: number;
  done: boolean;
}

export interface LegacyImage {
  url: string;
  kind: string;
  category_key: string;
}

export interface MediaWorkspace {
  unit?: { id: string; unit_number: string | null; unit_type: string | null; short_code: string | null };
  property?: { id: string; name: string | null; property_type: string | null; short_code: string | null };
  /** Shot-list family, or `building` for a property workspace. */
  family: string;
  checklist: { family: string; categories: MediaCategory[]; slots: ShotSlot[] };
  progress: {
    slots: SlotProgress[];
    overall: {
      required_done: number;
      required_total: number;
      required_slots_done: number;
      required_slots_total: number;
    };
  };
  items: MediaItem[];
  legacy_images: LegacyImage[];
  active_session: CaptureSession | null;
  /** Latest approved session — the agent's "approved, delete from your phone by …" banner. */
  last_approved_session?: CaptureSession | null;
}

export type { BilingualLabel };

export interface PartUrl {
  part_number: number;
  url: string;
}

const uploadInstructionsSchema = z.object({
  transport: z.enum(["presigned", "api"]),
  method: z.literal("PUT"),
  part_size: z.number().int().positive(),
  part_count: z.number().int().positive(),
  urls: z.array(z.object({ part_number: z.number().int(), url: z.string() })),
  expires_at: z.string(),
});
export type UploadInstructions = z.infer<typeof uploadInstructionsSchema>;

const createUploadResultSchema = z.object({
  item: mediaItemSchema,
  upload: uploadInstructionsSchema,
});
export type CreateUploadResult = z.infer<typeof createUploadResultSchema>;

const uploadStatusSchema = z.object({
  item: mediaItemSchema,
  upload: z.object({
    state: z.enum(["pending", "uploaded", "processing", "ready", "failed"]),
    part_size: z.number().int().positive(),
    part_count: z.number().int().positive(),
    uploaded_parts: z.array(z.object({ part_number: z.number().int(), size: z.number() })),
    missing_part_numbers: z.array(z.number().int()),
  }),
});
export type UploadStatus = z.infer<typeof uploadStatusSchema>;

const originalUrlSchema = z.object({
  url: z.string(),
  expires_at: z.string(),
  mime_type: z.string().nullable(),
  file_name: z.string().nullable(),
});
export type OriginalUrl = z.infer<typeof originalUrlSchema>;

// ── Review workflow (PR 4a wire shapes) ─────────────────────────────────────

const missingRequiredSchema = z.object({
  slot_key: z.string(),
  category_key: z.string(),
  required: z.number(),
  count: z.number(),
});
export type MissingRequiredSlotWire = z.infer<typeof missingRequiredSchema>;

const submitResultSchema = captureSessionSchema.extend({
  missing_required: z.array(missingRequiredSchema),
  moved_items: z.number(),
  not_ready_items: z.number(),
});
export type SubmitSessionResult = CaptureSession & {
  missing_required: MissingRequiredSlotWire[];
  moved_items: number;
  not_ready_items: number;
};

const displayNameSchema = z.object({
  id: z.string(),
  full_name: z.string().nullable(),
  full_name_en: z.string().nullable(),
});
export type UserDisplayName = z.infer<typeof displayNameSchema>;

const entitySummarySchema = z.object({
  type: z.enum(["unit", "property"]),
  id: z.string(),
  property_id: z.string().nullable(),
  unit_number: z.string().nullable(),
  unit_type: z.string().nullable(),
  name: z.string().nullable(),
  short_code: z.string().nullable(),
});
export type MediaEntitySummary = z.infer<typeof entitySummarySchema>;

const itemCountsSchema = z.object({
  draft: z.number(),
  in_review: z.number(),
  approved: z.number(),
  rejected: z.number(),
});

const reviewQueueEntrySchema = z.object({
  session: captureSessionSchema,
  entity: entitySummarySchema.nullable(),
  agent_id: z.string(),
  agent: displayNameSchema.nullable(),
  submitted_at: z.string().nullable(),
  counts: itemCountsSchema,
  missing_required: z.number().nullable(),
});
export type ReviewQueueEntry = z.infer<typeof reviewQueueEntrySchema> & { session: CaptureSession };

const sessionReviewSchema = z.object({
  session: captureSessionSchema,
  agent: displayNameSchema.nullable(),
  reviewer: displayNameSchema.nullable(),
  entity: entitySummarySchema,
  items: z.array(mediaItemSchema),
  checklist: workspaceSchema.shape.checklist,
  progress: workspaceSchema.shape.progress,
  missing_required: z.array(missingRequiredSchema),
});
export interface SessionReview {
  session: CaptureSession;
  agent: UserDisplayName | null;
  reviewer: UserDisplayName | null;
  entity: MediaEntitySummary;
  items: MediaItem[];
  checklist: MediaWorkspace["checklist"];
  progress: MediaWorkspace["progress"];
  missing_required: MissingRequiredSlotWire[];
}

/** The decided session; `gallery_sync_pending` is part of the session wire. */
export type SessionDecisionResult = CaptureSession;

export interface ReviewItemInput {
  decision: "approve" | "reject";
  reason?: MediaRejectReason;
  note?: string;
}

export interface SessionDecisionInput {
  decision: "approve" | "request_changes";
  note?: string;
}

// ── Request bodies (mirror the .strict() Zod DTOs) ───────────────────────────

export interface StartSessionInput {
  entity_type: MediaEntityType;
  entity_id: string;
}

export interface UpdateSessionInput {
  reference_measurements?: ReferenceMeasurement[];
  notes?: string | null;
  flags?: CaptureSessionFlags;
}

export interface UpdateItemInput {
  category_key?: string;
  slot_key?: string | null;
  is_cover?: boolean;
  sort_order?: number;
  visibility?: MediaVisibility;
}

export interface ImportLegacyInput {
  entity_type: MediaEntityType;
  entity_id: string;
  url: string;
  category_key: string;
  slot_key?: string;
}

export interface CreateUploadInput {
  entity_type: MediaEntityType;
  entity_id: string;
  session_id?: string;
  kind: MediaKind;
  mime_type: string;
  bytes: number;
  original_name: string;
  category_key?: string;
  slot_key?: string;
}

/** Per-attempt timeout of an `api`-transport part PUT (a 16 MiB body on a slow link). */
const PART_PUT_TIMEOUT_MS = 5 * 60 * 1000;

const json = (body: unknown) => JSON.stringify(body);

export const mediaCaptureRepository = {
  async getWorkspace(entityType: MediaEntityType, entityId: string): Promise<MediaWorkspace> {
    const path =
      entityType === "unit"
        ? API_ROUTES.mediaCapture.unitWorkspace(entityId)
        : API_ROUTES.mediaCapture.propertyWorkspace(entityId);
    const raw = await nestRequestScoped<unknown>(path);
    return workspaceSchema.parse(raw) as MediaWorkspace;
  },

  async startSession(input: StartSessionInput): Promise<CaptureSession & { created: boolean }> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.startSession(), {
      method: "POST",
      body: json(input),
    });
    const created = (raw as { created?: unknown })?.created === true;
    return { ...(captureSessionSchema.parse(raw) as CaptureSession), created };
  },

  async updateSession(id: string, input: UpdateSessionInput): Promise<CaptureSession> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.updateSession(id), {
      method: "PATCH",
      body: json(input),
    });
    return captureSessionSchema.parse(raw) as CaptureSession;
  },

  async updateItem(id: string, input: UpdateItemInput): Promise<MediaItem> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.updateItem(id), {
      method: "PATCH",
      body: json(input),
    });
    return mediaItemSchema.parse(raw);
  },

  async importLegacy(input: ImportLegacyInput): Promise<MediaItem> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.importLegacy(), {
      method: "POST",
      body: json(input),
    });
    return mediaItemSchema.parse(raw);
  },

  async deleteItem(id: string): Promise<void> {
    await nestRequest<void>(API_ROUTES.mediaCapture.deleteItem(id), { method: "DELETE" });
  },

  /** Reviewer variant (media.approve, company-scoped — no row scope). */
  async getReviewOriginalUrl(id: string): Promise<OriginalUrl> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.reviewOriginalUrl(id));
    return originalUrlSchema.parse(raw);
  },

  async getOriginalUrl(id: string): Promise<OriginalUrl> {
    const raw = await nestRequestScoped<unknown>(API_ROUTES.mediaCapture.originalUrl(id));
    return originalUrlSchema.parse(raw);
  },

  async createUpload(input: CreateUploadInput): Promise<CreateUploadResult> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.createUpload(), {
      method: "POST",
      body: json(input),
    });
    return createUploadResultSchema.parse(raw);
  },

  async getUploadStatus(itemId: string): Promise<UploadStatus> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.uploadStatus(itemId));
    return uploadStatusSchema.parse(raw);
  },

  async presignParts(itemId: string, partNumbers: number[]): Promise<UploadInstructions> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.uploadParts(itemId), {
      method: "POST",
      body: json({ part_numbers: partNumbers }),
    });
    return uploadInstructionsSchema.parse(raw);
  },

  /**
   * `api` transport (local-disk driver, dev/CI): PUT the raw part to the
   * API-relative `url` the server handed out, with the usual bearer token.
   * (makeNestRequest owns the fetch signal, so an abort lets an in-flight
   * part finish; the uploader stops before the next one.)
   */
  async putPartViaApi(url: string, body: Blob): Promise<void> {
    await nestRequestBinary<unknown>(url, {
      method: "PUT",
      body,
      headers: { "Content-Type": "application/octet-stream" },
      timeoutMs: PART_PUT_TIMEOUT_MS,
      retries: 0,
    });
  },

  async completeUpload(itemId: string): Promise<MediaItem> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.completeUpload(itemId), {
      method: "POST",
      timeoutMs: 60_000,
    });
    return mediaItemSchema.parse(raw);
  },

  async abortUpload(itemId: string): Promise<void> {
    await nestRequest<void>(API_ROUTES.mediaCapture.abortUpload(itemId), { method: "DELETE" });
  },

  // ── Review workflow ─────────────────────────────────────────────────────────
  // Agent submit = `properties.edit`; the reviewer endpoints = `media.approve`
  // (company-wide, no RowScopeGuard server-side → the unscoped binding).

  async submitSession(id: string): Promise<SubmitSessionResult> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.submitSession(id), { method: "POST" });
    return submitResultSchema.parse(raw) as SubmitSessionResult;
  },

  async getReviewQueue(): Promise<ReviewQueueEntry[]> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.reviewQueue());
    return z.array(reviewQueueEntrySchema).parse(raw) as ReviewQueueEntry[];
  },

  async getSessionReview(id: string): Promise<SessionReview> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.sessionReview(id));
    return sessionReviewSchema.parse(raw) as SessionReview;
  },

  async reviewItem(id: string, input: ReviewItemInput): Promise<MediaItem> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.reviewItem(id), {
      method: "POST",
      body: json(input),
    });
    return mediaItemSchema.parse(raw);
  },

  async decideSession(id: string, input: SessionDecisionInput): Promise<SessionDecisionResult> {
    const raw = await nestRequest<unknown>(API_ROUTES.mediaCapture.sessionDecision(id), {
      method: "POST",
      body: json(input),
      timeoutMs: 60_000,
    });
    return captureSessionSchema.parse(raw) as SessionDecisionResult;
  },
};
